import React, { useState } from 'react';
import { useLeaderboard, useRecentDonations } from '../hooks/useSupabase';

const LeaderboardStars = () => {
  const [activeTab, setActiveTab] = useState('leaderboard');
  const { data: leaderboard, loading: leaderboardLoading } = useLeaderboard(50);
  const { data: recentDonations, loading: donationsLoading } = useRecentDonations(20);

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1: return '👑';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return rank;
    }
  };

  const getRankColor = (rank) => {
    switch (rank) {
      case 1: return 'from-yellow-400 via-yellow-500 to-amber-500';
      case 2: return 'from-slate-300 via-slate-400 to-slate-500';
      case 3: return 'from-orange-400 via-orange-500 to-amber-600';
      default: return 'from-gray-100 to-gray-200';
    }
  };

  const getAchievementBadge = (totalDonations) => {
    if (totalDonations >= 100) return { title: 'Living Legend', color: 'from-purple-600 to-indigo-600', icon: '👑', glow: 'shadow-purple-500/50' };
    if (totalDonations >= 50) return { title: 'Guardian Angel', color: 'from-blue-500 to-cyan-500', icon: '😇', glow: 'shadow-blue-500/50' };
    if (totalDonations >= 25) return { title: 'Super Hero', color: 'from-red-500 to-rose-600', icon: '🦸', glow: 'shadow-red-500/50' };
    if (totalDonations >= 10) return { title: 'Life Saver', color: 'from-emerald-500 to-green-600', icon: '🩺', glow: 'shadow-emerald-500/50' };
    if (totalDonations >= 5) return { title: 'Helping Hand', color: 'from-amber-400 to-yellow-500', icon: '🤝', glow: 'shadow-amber-500/50' };
    if (totalDonations >= 1) return { title: 'First Drop', color: 'from-pink-400 to-rose-500', icon: '💧', glow: 'shadow-pink-500/50' };
    return null;
  };

  const PodiumCard = ({ donor, rank, height }) => {
    const badge = getAchievementBadge(donor?.totalDonations || 0);
    const isFirst = rank === 1;

    return (
      <div className={`relative flex flex-col items-center ${isFirst ? 'z-10' : 'z-0'}`}>
        {/* Crown for #1 */}
        {isFirst && (
          <div className="absolute -top-8 animate-bounce">
            <span className="text-4xl">👑</span>
          </div>
        )}

        {/* Avatar */}
        <div className={`w-16 h-16 ${isFirst ? 'w-20 h-20' : 'w-16 h-16'} rounded-full bg-gradient-to-br ${getRankColor(rank)} flex items-center justify-center shadow-xl mb-3 border-4 border-white ring-4 ${rank === 1 ? 'ring-yellow-300' : rank === 2 ? 'ring-slate-300' : 'ring-orange-300'}`}>
          <span className="text-2xl font-bold text-white">{donor?.name?.[0]?.toUpperCase() || '?'}</span>
        </div>

        {/* Info Card */}
        <div className={`bg-white rounded-2xl shadow-xl p-4 text-center min-w-[140px] ${isFirst ? 'min-w-[180px] p-5' : ''} border border-gray-100`}>
          <div className="text-2xl mb-1">{getRankIcon(rank)}</div>
          <h4 className={`font-bold text-gray-800 ${isFirst ? 'text-lg' : 'text-sm'} truncate`}>{donor?.name}</h4>
          <p className="text-xs text-gray-500 mb-2">{donor?.district}</p>

          {badge && (
            <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold text-white bg-gradient-to-r ${badge.color} shadow-lg ${badge.glow} mb-2`}>
              <span>{badge.icon}</span>
              <span>{badge.title}</span>
            </div>
          )}

          <div className="flex justify-center gap-3 text-sm">
            <div className="text-center">
              <div className="font-bold text-red-600">{donor?.totalDonations || 0}</div>
              <div className="text-[10px] text-gray-400 uppercase">Donations</div>
            </div>
            <div className="text-center">
              <div className="font-bold text-blue-600">{donor?.points || 0}</div>
              <div className="text-[10px] text-gray-400 uppercase">Points</div>
            </div>
          </div>
        </div>

        {/* Podium Base */}
        <div className={`w-full mt-2 rounded-t-lg bg-gradient-to-t ${getRankColor(rank)} shadow-lg flex items-end justify-center pb-2 text-white font-bold ${height}`}>
          <span className="text-2xl drop-shadow-lg">{rank}</span>
        </div>
      </div>
    );
  };

  const renderLeaderboard = () => (
    <div className="space-y-6">
      {/* Top 3 Podium */}
      {leaderboard && leaderboard.length >= 3 && (
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-b from-rose-50 to-transparent rounded-3xl"></div>
          <div className="relative py-8 px-4">
            <div className="flex justify-center items-end gap-3 md:gap-6">
              {/* 2nd Place - Left */}
              <PodiumCard donor={leaderboard[1]} rank={2} height="h-24" />

              {/* 1st Place - Center (taller) */}
              <PodiumCard donor={leaderboard[0]} rank={1} height="h-32" />

              {/* 3rd Place - Right */}
              <PodiumCard donor={leaderboard[2]} rank={3} height="h-20" />
            </div>
          </div>
        </div>
      )}

      {/* Full Leaderboard Table */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-slate-50 to-gray-50 border-b border-gray-100">
          <h4 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <span className="text-2xl">⭐</span> All Donor Stars
          </h4>
        </div>

        <div className="divide-y divide-gray-100">
          {leaderboardLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin w-8 h-8 border-4 border-red-200 border-t-red-500 rounded-full mx-auto mb-3"></div>
              <p className="text-gray-500">Loading heroes...</p>
            </div>
          ) : leaderboard && leaderboard.length > 0 ? (
            leaderboard.map((donor, index) => {
              const rank = index + 1;
              const badge = getAchievementBadge(donor.totalDonations || 0);
              const isTop3 = rank <= 3;

              return (
                <div
                  key={donor.id}
                  className={`p-4 transition-all hover:bg-gradient-to-r ${isTop3 ? 'bg-gradient-to-r from-yellow-50/50 via-white to-yellow-50/50' : 'hover:from-red-50/30 hover:to-transparent'} group`}
                >
                  <div className="flex items-center gap-4">
                    {/* Rank */}
                    <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center font-bold text-lg ${
                      isTop3
                        ? 'bg-gradient-to-br ' + getRankColor(rank) + ' text-white shadow-lg'
                        : 'bg-gray-100 text-gray-600 group-hover:bg-white group-hover:shadow-md'
                    }`}>
                      {rank <= 3 ? getRankIcon(rank) : rank}
                    </div>

                    {/* Avatar & Info */}
                    <div className="flex-1 flex items-center gap-3 min-w-0">
                      <div className={`w-10 h-10 rounded-full bg-gradient-to-br from-red-400 to-rose-500 flex items-center justify-center text-white font-bold text-sm shadow-md`}>
                        {donor.name?.[0]?.toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-gray-900 truncate">{donor.name}</div>
                        <div className="text-xs text-gray-500 flex items-center gap-2">
                          <span className="px-1.5 py-0.5 bg-red-50 text-red-600 rounded text-[10px] font-bold">{donor.bloodGroup}</span>
                          <span>•</span>
                          <span className="truncate">{donor.district}</span>
                        </div>
                      </div>
                    </div>

                    {/* Badge */}
                    {badge && (
                      <div className="hidden sm:block">
                        <div className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold text-white bg-gradient-to-r ${badge.color} shadow-md`}>
                          <span>{badge.icon}</span>
                          <span className="hidden md:inline">{badge.title}</span>
                        </div>
                      </div>
                    )}

                    {/* Stats */}
                    <div className="flex items-center gap-4 text-right">
                      <div className="text-center min-w-[60px]">
                        <div className="text-lg font-bold text-red-600">{donor.totalDonations || 0}</div>
                        <div className="text-[10px] text-gray-400 uppercase tracking-wide">Donations</div>
                      </div>
                      <div className="text-center min-w-[60px]">
                        <div className="text-lg font-bold text-blue-600">{donor.points || 0}</div>
                        <div className="text-[10px] text-gray-400 uppercase tracking-wide">Points</div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="p-12 text-center">
              <div className="text-6xl mb-4">🏆</div>
              <h3 className="text-xl font-bold text-gray-700 mb-2">No Stars Yet</h3>
              <p className="text-gray-500">Be the first to donate blood and become a hero!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderRecentDonations = () => (
    <div className="space-y-6">
      {/* Timeline Style */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-red-50 to-rose-50 border-b border-gray-100">
          <h4 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <span className="text-2xl">🩸</span> Recent Life-Saving Donations
          </h4>
        </div>

        <div className="divide-y divide-gray-100">
          {donationsLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin w-8 h-8 border-4 border-red-200 border-t-red-500 rounded-full mx-auto mb-3"></div>
              <p className="text-gray-500">Loading recent donations...</p>
            </div>
          ) : recentDonations && recentDonations.length > 0 ? (
            <div className="max-h-[600px] overflow-y-auto">
              {recentDonations.map((donation, index) => (
                <div key={index} className="p-4 hover:bg-gradient-to-r hover:from-red-50/30 hover:to-transparent transition-all group">
                  <div className="flex items-center gap-4">
                    {/* Donor Avatar */}
                    <div className="relative">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-400 to-rose-500 flex items-center justify-center text-white font-bold shadow-lg">
                        {donation.donorName?.[0]?.toUpperCase()}
                      </div>
                      <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white text-xs border-2 border-white">
                        ✓
                      </div>
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-900">{donation.donorName}</span>
                        <span className="text-gray-400">donated at</span>
                        <span className="font-semibold text-red-600">{donation.hospital}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-sm text-gray-600">{donation.units} unit{donation.units > 1 ? 's' : ''}</span>
                        {donation.urgency !== 'normal' && (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            donation.urgency === 'critical'
                              ? 'bg-red-100 text-red-700 border border-red-200'
                              : 'bg-orange-100 text-orange-700 border border-orange-200'
                          }`}>
                            {donation.urgency === 'critical' ? '🚨' : '⚡'} {donation.urgency}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        {donation.recordedAt && new Date(donation.recordedAt.toDate?.() || donation.recordedAt).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>

                    {/* Points Badge */}
                    <div className="flex-shrink-0">
                      <div className="bg-gradient-to-br from-green-400 to-emerald-500 text-white rounded-xl px-3 py-2 shadow-lg text-center">
                        <div className="text-lg font-bold">+{donation.pointsEarned}</div>
                        <div className="text-[10px] font-medium">POINTS</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center">
              <div className="text-6xl mb-4">🩸</div>
              <h3 className="text-xl font-bold text-gray-700 mb-2">No Recent Donations</h3>
              <p className="text-gray-500">Recent blood donations will appear here.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderStats = () => {
    // Only count donors who have actually donated (totalDonations > 0)
    const heroDonors = leaderboard?.filter(d => (d.totalDonations || 0) > 0) || [];
    const totalHeroDonors = heroDonors.length;
    const totalPoints = heroDonors.reduce((sum, donor) => sum + (donor.points || 0), 0);
    const totalDonations = heroDonors.reduce((sum, donor) => sum + (donor.totalDonations || 0), 0);
    const avgPointsPerDonor = totalHeroDonors > 0 ? Math.round(totalPoints / totalHeroDonors) : 0;

    const statCards = [
      { label: 'Hero Donors', value: totalHeroDonors, color: 'from-red-500 to-rose-600', icon: '⭐', shadow: 'shadow-red-200' },
      { label: 'Lives Saved', value: totalDonations, color: 'from-blue-500 to-cyan-600', icon: '❤️', shadow: 'shadow-blue-200' },
      { label: 'Total Points', value: totalPoints.toLocaleString(), color: 'from-amber-400 to-orange-500', icon: '🏆', shadow: 'shadow-amber-200' },
      { label: 'Avg Points', value: avgPointsPerDonor, color: 'from-emerald-500 to-green-600', icon: '📊', shadow: 'shadow-emerald-200' },
    ];

    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {statCards.map((stat, idx) => (
          <div key={idx} className={`relative overflow-hidden bg-white rounded-2xl shadow-lg ${stat.shadow} border border-gray-100 p-5 group hover:scale-105 transition-transform`}>
            <div className={`absolute -right-4 -top-4 w-20 h-20 rounded-full bg-gradient-to-br ${stat.color} opacity-10 group-hover:opacity-20 transition-opacity`}></div>
            <div className="relative">
              <div className="text-2xl mb-2">{stat.icon}</div>
              <div className={`text-3xl font-black bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}>
                {stat.value}
              </div>
              <div className="text-sm text-gray-500 font-medium mt-1">{stat.label}</div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50/30">
      {/* Hero Header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-red-600 via-rose-600 to-red-700"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.05%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-50"></div>
        <div className="relative max-w-6xl mx-auto px-6 py-12 md:py-16">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm font-medium mb-4">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
              Celebrating Our Heroes
            </div>
            <h1 className="text-4xl md:text-5xl font-black text-white mb-4 tracking-tight">
              Blood Donation <span className="text-yellow-300">Hall of Fame</span>
            </h1>
            <p className="text-white/80 text-lg max-w-2xl mx-auto">
              Honoring the selfless heroes who donate blood and save lives every day.
              Every drop counts. Every donor matters.
            </p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-slate-50 to-transparent"></div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 -mt-8 relative z-10">
        {renderStats()}
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-12">
        {/* Modern Tabs */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex p-1 bg-gray-100/80 backdrop-blur-sm rounded-2xl shadow-inner">
            <button
              onClick={() => setActiveTab('leaderboard')}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-all ${
                activeTab === 'leaderboard'
                  ? 'bg-white text-red-600 shadow-lg scale-105'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <span className="text-lg">🏆</span> Leaderboard
            </button>
            <button
              onClick={() => setActiveTab('recent')}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-all ${
                activeTab === 'recent'
                  ? 'bg-white text-red-600 shadow-lg scale-105'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <span className="text-lg">🩸</span> Recent Donations
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'leaderboard' && renderLeaderboard()}
        {activeTab === 'recent' && renderRecentDonations()}

        {/* Achievement Levels - Modern Cards */}
        <div className="mt-12">
          <div className="text-center mb-6">
            <h3 className="text-2xl font-bold text-gray-800">Achievement Levels</h3>
            <p className="text-gray-500">Unlock badges as you save more lives</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { title: 'First Drop', donations: '1', icon: '💧', color: 'from-pink-400 to-rose-500', bg: 'bg-pink-50' },
              { title: 'Helping Hand', donations: '5', icon: '🤝', color: 'from-amber-400 to-yellow-500', bg: 'bg-amber-50' },
              { title: 'Life Saver', donations: '10', icon: '🩺', color: 'from-emerald-400 to-green-500', bg: 'bg-emerald-50' },
              { title: 'Super Hero', donations: '25', icon: '🦸', color: 'from-red-500 to-rose-600', bg: 'bg-red-50' },
              { title: 'Guardian Angel', donations: '50', icon: '😇', color: 'from-blue-400 to-cyan-500', bg: 'bg-blue-50' },
              { title: 'Living Legend', donations: '100', icon: '👑', color: 'from-purple-500 to-indigo-600', bg: 'bg-purple-50' }
            ].map((level, index) => (
              <div key={index} className={`${level.bg} rounded-2xl p-4 text-center hover:shadow-xl transition-all hover:scale-105 border border-gray-100`}>
                <div className={`w-14 h-14 bg-gradient-to-br ${level.color} rounded-2xl flex items-center justify-center text-white text-2xl mx-auto mb-3 shadow-lg`}>
                  {level.icon}
                </div>
                <div className="font-bold text-gray-800 text-sm">{level.title}</div>
                <div className="text-xs text-gray-500 mt-1">{level.donations}+ donations</div>
              </div>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-12 bg-gradient-to-r from-red-600 to-rose-600 rounded-2xl p-8 text-center text-white shadow-xl">
          <h3 className="text-2xl font-bold mb-2">Be a Hero Today!</h3>
          <p className="text-white/80 mb-4 max-w-lg mx-auto">
            Every blood donation can save up to 3 lives. Join our community of life-savers and make a difference.
          </p>
          <a href="/register" className="inline-flex items-center gap-2 bg-white text-red-600 px-6 py-3 rounded-xl font-bold hover:bg-gray-100 transition-colors shadow-lg">
            🩸 Register as Donor
          </a>
        </div>
      </div>
    </div>
  );
};

export default LeaderboardStars;
